import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AppPageContainer } from '@/modules/shared/components/ui/AppPageContainer';
import { AdminPageHeader } from '@/modules/shared/components/AdminPageHeader';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { SUPERADMIN_ROUTES } from '@/modules/superadmin/constants/routes';
import { SuperAdminPageContainer } from '@/modules/shared/components/superadmin';
import { Settings, Mail, Palette, FileText, LogIn, Bell, BookOpenText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SettingsDirtyProvider, useSettingsDirty } from '../contexts/SettingsDirtyContext';

const tabs = [
  {
    label: 'Application Settings',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS,
    icon: Settings,
    end: true, // exact match
  },
  {
    label: 'Login Page',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS_LOGIN_PAGE,
    icon: LogIn,
    end: false,
  },
  {
    label: 'Email Templates',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS_EMAIL_TEMPLATES,
    icon: Mail,
    end: false,
  },
  {
    label: 'UI Customization',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS_UI,
    icon: Palette,
    end: false,
  },
  {
    label: 'Instruction Manager',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS_INSTRUCTION_MANAGER,
    icon: BookOpenText,
    end: false,
  },
  {
    label: 'Legal Pages',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS_LEGAL_PAGES,
    icon: FileText,
    end: false,
  },
  {
    label: 'Notification Engine',
    path: SUPERADMIN_ROUTES.APPLICATION_SETTINGS_NOTIFICATION_ENGINE,
    icon: Bell,
    end: false,
  },
] as const;

export default function ApplicationSettingsLayout() {
  return (
    <SettingsDirtyProvider>
      <ApplicationSettingsLayoutContent />
    </SettingsDirtyProvider>
  );
}

function ApplicationSettingsLayoutContent() {
  const location = useLocation();
  const { isDirty } = useSettingsDirty();

  return (
    <SuperAdminPageContainer pageName="Application Settings" maxWidth="xxl">
      <AppPageContainer
        maxWidth="full"
        noPadding
      >
        <AdminPageHeader
          title="Application Settings"
          description="SuperAdmin — Configure system-wide settings"
        />
        {/* Tab Bar */}
        <div className="border-b border-border">
          <nav className="flex gap-0 -mb-px" aria-label="Settings tabs">
            {tabs.map((tab) => {
              const isActive = tab.end
                ? location.pathname === tab.path
                : location.pathname.startsWith(tab.path);

              return (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  end={tab.end}
                  onClick={(e) => {
                    if (isDirty && !window.confirm('You have unsaved changes on this page. Leave and discard them?')) {
                      e.preventDefault();
                    }
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-medium border-b-2 transition-colors whitespace-nowrap',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  )}
                >
                  <AppIcon Icon={tab.icon} size="compact" variant={isActive ? 'primary' : 'default'} />
                  {tab.label}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Tab Content — rendered by nested route */}
        <Outlet />
      </AppPageContainer>
    </SuperAdminPageContainer>
  );
}
